import React, { useEffect, useState } from 'react';
import { Card, Stack, Typography } from '@mui/material';
import { Order } from '@api/Orders/Order';
import { LineChart } from '@mui/x-charts';
import TextButton from '@components/Utilities/Buttons/TextButton';
import { ChartsAxisContentProps } from '@mui/x-charts/ChartsTooltip/ChartsAxisTooltipContent';
interface SalesPerHourProps {
    orders: Order[];
}

const daysLabels = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];

const monthLabels = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre'
];
const SalesPerHour: React.FunctionComponent<SalesPerHourProps> = (props) => {
    const { orders } = props;
    const [option, setOption] = useState<number>(0);
    const [sales, setSales] = useState<number[]>([]);
    const [salesTotals, setSalesTotals] = useState<string[]>([]);

    const getOptionNumber = () => {
        switch (option) {
            case 0:
                return 24;
            case 1:
                return 7;
            case 2:
                return 4;
            case 3:
                return 12;
        }
        return 24;
    };

    const getValueFormatter = (value: number) => {
        switch (option) {
            case 0:
                return `${value}:00 hrs`;
            case 1:
                if (Number.isInteger(value)) {
                    return daysLabels[value];
                }
                return '';
            case 2:
                if (Number.isInteger(value)) {
                    return `Semana ${value + 1}`;
                }
                return '';
            case 3:
                if (Number.isInteger(value)) {
                    return monthLabels[value];
                }
                return '';
        }
        return `${value} hrs`;
    };

    useEffect(() => {
        const arrayCount: number[] = [];
        const arrayTotals: string[] = [];
        for (let i = 0; i < getOptionNumber(); i++) {
            let ordersFilter: Order[] = [];
            switch (option) {
                case 0:
                    ordersFilter = orders.filter((order) => order.date.toDate().getHours() === i);
                    break;
                case 1:
                    ordersFilter = orders.filter((order) => {
                        let day = order.date.toDate().getDay() - 1;
                        if (day < 0) {
                            day = 6;
                        }
                        return day === i;
                    });
                    break;
                case 2:
                    ordersFilter = orders.filter((order) => {
                        const date = order.date.toDate() as Date;
                        const day = date.getDate();
                        let week = 0;
                        if (day <= 7) {
                            week = 0;
                        } else if (day >= 8 && day <= 14) {
                            week = 1;
                        } else if (day >= 15 && day <= 21) {
                            week = 2;
                        } else if (day >= 22) {
                            week = 3;
                        }
                        return week === i;
                    });
                    break;
                case 3:
                    ordersFilter = orders.filter((order) => {
                        const date = order.date.toDate() as Date;
                        const month = date.getMonth();
                        return month === i;
                    });
                    break;
            }
            const count = ordersFilter.length;
            const total = ordersFilter.reduce((a, o) => a + (o.total || 0), 0).toFixed(2);
            arrayCount.push(count);
            arrayTotals.push(total);
        }
        setSales(arrayCount);
        setSalesTotals(arrayTotals);
    }, [orders, option]);

    const CustomItemTooltipContent = (props: ChartsAxisContentProps) => {
        const { series, dataIndex } = props;

        const index = dataIndex || 0;

        const value = series[0].data[index];

        const sales = series[0];

        if (value === 0) {
            return null;
        }

        return (
            <Stack component={Card} p={1} px={2} bgcolor="#242128">
                <Typography color="white" fontSize={16}>
                    {getValueFormatter(index)}
                </Typography>
                <Typography color="white" fontSize={20} fontWeight={600}>
                    {`$${salesTotals[index]}`}
                </Typography>
                <Typography color="white" fontSize={16}>
                    {sales.valueFormatter(value as any)}
                </Typography>
            </Stack>
        );
    };

    return (
        <Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Typography fontSize={20} fontWeight={700}>
                    Ventas por
                </Typography>
                <TextButton selected={option === 0} onClick={() => setOption(0)}>
                    Hora
                </TextButton>
                <TextButton selected={option === 1} onClick={() => setOption(1)}>
                    Día
                </TextButton>
                <TextButton selected={option === 2} onClick={() => setOption(2)}>
                    Semana
                </TextButton>
                <TextButton selected={option === 3} onClick={() => setOption(3)}>
                    Año
                </TextButton>
            </Stack>
            <LineChart
                xAxis={[
                    {
                        data: Array.from(Array(getOptionNumber()).keys()),
                        scaleType: 'linear',
                        valueFormatter: (value: number) => getValueFormatter(value)
                    }
                ]}
                series={[
                    {
                        data: sales.length === getOptionNumber() ? sales : Array.from(Array(getOptionNumber()).keys()),
                        showMark: ({ value }) => value > 0,
                        valueFormatter: (value: number) => `${value} ventas`
                    }
                ]}
                height={300}
                tooltip={{ trigger: 'axis', axisContent: CustomItemTooltipContent }}
            />
        </Stack>
    );
};

export default SalesPerHour;
